import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ApiClient from "../../config/AppAdmin";
import dynamicSocketUrl from "../../config/socketUrl";

export const fetchWaiterData = createAsyncThunk(
  "waiter/fetchWaiterData",
  async () => {
    const response = await ApiClient.get(`/waiter`);
    // console.log("reponse of waiter",response.data.data);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error("Invalid data format");
  }
);

const waiterSlice = createSlice({
  name: "waiter",
  initialState: {
    data: [],
    orders: [],
    loading: false,
    error: null,
    sseConnected: false,
    sseError: null,
    notifications: [],
    lastVisited: null,
    unreadOrders: 0,
    unseenOrders: [] // Track orders that haven't been viewed yet
  },
  reducers: {
    // Reducer for new orders from SSE
    newWaiterOrderReceived: (state, action) => {
      const newOrder = action.payload[0] || action.payload;
      if (newOrder && newOrder.order_id) {
        // Update orders list
        state.orders = state.orders.filter(
          (o) => o.order_id !== newOrder.order_id
        );
        state.orders = [newOrder, ...state.orders];

        // Add to unseen orders if not already there
        if (!state.unseenOrders.some(o => o.order_id === newOrder.order_id)) {
          state.unseenOrders.push({ ...newOrder, seen: false });
        }

        // Update notifications
        if (!state.orders.some((o) => o.order_id === newOrder.order_id)) {
          state.notifications.push({
            id: Date.now(),
            type: "newOrder",
            message: `New order received for Table ${newOrder.table_number}`,
            orderId: newOrder.order_id,
            timestamp: new Date().toISOString(),
            isNew: true,
          });
        }

        // Increment unread count
        state.unreadOrders += 1;
      }
    },
    // Mark all orders as seen
    markOrdersAsSeen: (state) => {
      state.unseenOrders = state.unseenOrders.map(order => ({ ...order, seen: true }));
      state.unreadOrders = 0;
      state.lastVisited = new Date().toISOString();
    },
    // Mark orders as read (kept for backward compatibility)
    markOrdersAsRead: (state) => {
      state.unreadOrders = 0;
      state.lastVisited = new Date().toISOString();
      state.notifications = state.notifications.map((n) => ({
        ...n,
        isNew: false,
      }));
      // Also mark unseen orders as seen
      state.unseenOrders = state.unseenOrders.map(order => ({ ...order, seen: true }));
    },
    // Add individual unseen order
    addUnseenOrder: (state, action) => {
      const newOrder = action.payload;
      if (!state.unseenOrders.some(order => order.order_id === newOrder.order_id)) {
        state.unseenOrders.push({ ...newOrder, seen: false });
        state.unreadOrders += 1;
      }
    },
    // Clear unseen orders for a specific order
    clearUnseenOrder: (state, action) => {
      const orderId = action.payload;
      state.unseenOrders = state.unseenOrders.filter(o => o.order_id !== orderId);
    },
    sseConnectionEstablished: (state) => {
      state.sseConnected = true;
      state.sseError = null;
    },
    sseConnectionClosed: (state) => {
      state.sseConnected = false;
    },
    sseConnectionError: (state, action) => {
      state.sseConnected = false;
      state.sseError = action.payload;
    },
    clearWaiterOrders: (state) => {
      state.orders = [];
    },
    clearNotification: (state, action) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },
    clearAllNotifications: (state) => {
      state.notifications = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWaiterData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWaiterData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchWaiterData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

// Export all actions
export const {
  newWaiterOrderReceived,
  sseConnectionEstablished,
  sseConnectionClosed,
  sseConnectionError,
  clearWaiterOrders,
  clearNotification,
  clearAllNotifications,
  markOrdersAsRead,
  markOrdersAsSeen,
  addUnseenOrder,
  clearUnseenOrder,
} = waiterSlice.actions;

export const initializeWaiterOrdersSSE = () => (dispatch) => {
  const eventSource = new EventSource(`${dynamicSocketUrl()}/bbgapi/sse`, {
    withCredentials: true,
  });

  eventSource.onopen = () => {
    // console.log("SSE connection established");
    dispatch(sseConnectionEstablished());
  };

  eventSource.addEventListener("waiterOrder", (event) => {
    try {
      const parsedData = JSON.parse(event.data);
      // console.log("Received order:", parsedData.order);
      // Dispatch both actions to maintain all functionality
      dispatch(newWaiterOrderReceived(parsedData.order));
      dispatch(addUnseenOrder(parsedData.order));
    } catch (error) {
      console.error("Error parsing SSE data:", error);
    }
  });

  eventSource.onerror = (error) => {
    // console.error("SSE error:", error);
    dispatch(sseConnectionError(error.message || "SSE connection error"));
    eventSource.close();
  };

  return () => {
    eventSource.close();
    dispatch(sseConnectionClosed());
  };
};

export default waiterSlice.reducer;